import { POSTS_META } from "./posts-data.ts";
import { POSTS_META_ES } from "./posts-data.es.ts";

export type BlogLocale = "en" | "es";

export interface BlogPostMeta {
  slug: string;
  title: string;
  category: "Axles & Suspension" | "Service & Repair" | "Buyer Guides" | "Trailer Parts";
  date: string;
  dateIso: string;
  excerpt: string;
  readMinutes: number;
}

export interface BlogPostContent {
  locale?: BlogLocale;
  slug: string;
  toc?: { id: string; label: string }[];
  html: string;
}

export type BlogPost = BlogPostMeta & Partial<BlogPostContent>;

/** Cover image path for a post - generated by scripts/generate-blog-data.mjs. */
export function postImage(slug: string, locale: BlogLocale = "en"): string {
  return `${locale === "es" ? "/es" : ""}/blog/${slug}.svg`;
}

export const POSTS: BlogPostMeta[] = POSTS_META;

export function getPosts(locale: BlogLocale = "en"): BlogPostMeta[] {
  return locale === "es" ? POSTS_META_ES : POSTS;
}

export function getPost(slug: string, locale: BlogLocale = "en"): BlogPostMeta | undefined {
  return getPosts(locale).find((p) => p.slug === slug);
}

// ---------------------------------------------------------------------------
// Lazy post-body loading
// ---------------------------------------------------------------------------
//
// The article HTML for every post lives in public/blog/content/<slug>.json so
// listing pages (home, blog index, location pages) only pay the cost of post
// metadata. The full body is fetched on demand when a visitor opens a post.
//
// During SSR/prerender, the prerender script reads the JSON from disk and
// hands the content to the render entry, which seeds the cache below and
// embeds an inline script so client hydration sees the same HTML without a
// flash or layout shift.

const ssrContentCache = new Map<string, BlogPostContent>();

/** Server-side: stash content for a slug so the post page can read it
 * synchronously during renderToString. */
export function provideSsrPostContent(content: BlogPostContent): void {
  ssrContentCache.clear();
  ssrContentCache.set(`${content.locale ?? "en"}:${content.slug}`, content);
}

interface PostContentWindow {
  __AXLE_POST_CONTENT__?: BlogPostContent;
}

/** Synchronously returns content already available in the current process —
 * the SSR cache on the server, the hydration payload, or a previously
 * prefetched/fetched body on the client. */
export function getPreloadedPostContent(slug: string, locale: BlogLocale = "en"): BlogPostContent | undefined {
  const key = `${locale}:${slug}`;
  if (typeof window !== "undefined") {
    const payload = (window as unknown as PostContentWindow).__AXLE_POST_CONTENT__;
    if (payload && payload.slug === slug && (payload.locale ?? "en") === locale) return payload;
    return fetchedContent.get(key);
  }
  return ssrContentCache.get(key);
}

function contentUrl(slug: string, locale: BlogLocale): string {
  const base =
    (typeof import.meta !== "undefined" &&
      (import.meta as unknown as { env?: { BASE_URL?: string } }).env?.BASE_URL) ||
    "/";
  const normalized = base.endsWith("/") ? base : `${base}/`;
  return `${normalized}${locale === "es" ? "es/" : ""}blog/content/${encodeURIComponent(slug)}.json`;
}

const inflightContent = new Map<string, Promise<BlogPostContent | undefined>>();
const fetchedContent = new Map<string, BlogPostContent>();

function doFetch(slug: string, priority: "auto" | "low", locale: BlogLocale): Promise<BlogPostContent | undefined> {
  const key = `${locale}:${slug}`;
  const existing = inflightContent.get(key);
  if (existing) return existing;
  const cached = fetchedContent.get(key);
  if (cached) return Promise.resolve(cached);
  if (typeof fetch === "undefined") return Promise.resolve(undefined);
  const init: RequestInit & { priority?: "auto" | "low" | "high" } = { priority };
  const p = fetch(contentUrl(slug, locale), init)
    .then(async (res) => {
      if (!res.ok) return undefined;
      const data = (await res.json()) as BlogPostContent;
      if (data.slug !== slug || (data.locale ?? "en") !== locale || typeof data.html !== "string")
        throw new Error(`Invalid ${locale} blog payload: ${slug}`);
      fetchedContent.set(key, data);
      return data;
    })
    .catch(() => undefined)
    .finally(() => {
      inflightContent.delete(key);
    });
  inflightContent.set(key, p);
  return p;
}

/** Browser-side: fetch the post body if it isn't already preloaded. */
export async function fetchPostContent(slug: string, locale: BlogLocale = "en"): Promise<BlogPostContent | undefined> {
  const preloaded = getPreloadedPostContent(slug, locale);
  if (preloaded) return preloaded;
  return doFetch(slug, "auto", locale);
}

/** Warm the cache for a post's body. Safe to call repeatedly; in-flight and
 * resolved fetches are de-duplicated. Uses low fetch priority so it never
 * competes with the user's current navigation. */
export function prefetchPostContent(slug: string, locale: BlogLocale = "en"): void {
  if (typeof window === "undefined") return;
  if (getPreloadedPostContent(slug, locale)) return;
  const key = `${locale}:${slug}`;
  if (fetchedContent.has(key) || inflightContent.has(key)) return;
  void doFetch(slug, "low", locale);
}
